/**
 * Badge de version — affiché en petit à côté du logo dans le header.
 *
 * Objectif : que chaque utilisateur puisse dire « je suis sur la vX » et
 * qu'on sache immédiatement s'il est à jour, sans aucune gestion manuelle
 * de numéro de version.
 *
 * - « v868 » : version du FRONTEND chargé, injectée au build (compteur de
 *   commits, cf. vite.config.js). Change à chaque déploiement frontend.
 * - « api 214 » : révision du BACKEND, lue sur /api/version (révision Cloud
 *   Run) et rafraîchie périodiquement → se met à jour TOUTE SEULE quand le
 *   backend redéploie, sans recharger la page.
 * - Flèche ambre : un NOUVEAU frontend est servi (le meta x-commit du serveur
 *   diffère du commit chargé) → un clic recharge l'appli. Vérifié
 *   périodiquement et au retour sur l'onglet.
 */
import { useEffect, useRef, useState } from 'react';
import { API_URL } from '../api/client';
import { copierDansPressePapiers } from '../utils/pressePapiers';

const POLL_MS = 5 * 60 * 1000; // 5 minutes

export default function VersionBadge() {
  const [apiVersion, setApiVersion] = useState(null);
  // 'idle' | 'ok' | 'err' — l'échec de copie a son propre état, pour être DIT.
  const [copyState, setCopyState] = useState('idle');
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const timerRef = useRef(null);
  const copyTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      // 1. Version du backend (publique, sans auth)
      try {
        const r = await fetch(`${API_URL}/api/version`, { cache: 'no-store' });
        if (r.ok) {
          const d = await r.json();
          if (!cancelled) setApiVersion(d.number ?? d.revision ?? null);
        }
      } catch { /* backend injoignable : on garde la dernière valeur */ }

      // 2. Nouveau frontend servi ? On relit le HTML de notre propre origine
      //    et on compare son meta x-commit au commit embarqué dans ce build.
      try {
        const r = await fetch(`/?v=${Date.now()}`, { cache: 'no-store' });
        const html = await r.text();
        const m = html.match(/name="x-commit" content="([a-f0-9]+)"/);
        if (!cancelled && m && m[1] !== __APP_COMMIT__) setUpdateAvailable(true);
      } catch { /* offline : on réessaiera */ }
    };

    check();
    timerRef.current = setInterval(check, POLL_MS);
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      cancelled = true;
      clearInterval(timerRef.current);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, []);

  useEffect(() => () => clearTimeout(copyTimerRef.current), []);

  const versionTxt = `v${__APP_VERSION__}${apiVersion != null ? ` · api ${apiVersion}` : ''}`;

  const handleCopy = async () => {
    const ok = await copierDansPressePapiers(versionTxt);
    setCopyState(ok ? 'ok' : 'err');
    clearTimeout(copyTimerRef.current);
    // L'échec reste affiché plus longtemps : il demande une action manuelle.
    copyTimerRef.current = setTimeout(() => setCopyState('idle'), ok ? 1500 : 7000);
  };

  return (
    <div className="relative flex items-center gap-1.5 shrink-0" title={`Frontend v${__APP_VERSION__} (${__APP_COMMIT__}, build ${__APP_BUILD_DATE__})${apiVersion ? ` — Backend r${apiVersion}` : ''}`}>
      {/* Sélectionnable : c'est le repli manuel proposé si la copie échoue. */}
      <span className="text-[9px] font-mono text-white/40 tracking-wider leading-none whitespace-nowrap uppercase select-all">
        v{__APP_VERSION__}{apiVersion != null && <span className="text-white/30"> · api {apiVersion}</span>}
      </span>
      {/* Copier la version — pour la coller telle quelle dans un message */}
      <button
        onClick={handleCopy}
        title={
          copyState === 'ok' ? 'Copié !'
          : copyState === 'err' ? "Copie impossible dans ce navigateur : sélectionnez le numéro de version affiché à gauche et copiez-le à la main."
          : 'Copier le numéro de version'
        }
        aria-label="Copier le numéro de version"
        className={`w-4 h-4 rounded flex items-center justify-center transition-colors shrink-0 ${
          copyState === 'ok' ? 'text-emerald-400'
          : copyState === 'err' ? 'text-amber-400'
          : 'text-white/25 hover:text-white/70 hover:bg-white/10'
        }`}
      >
        {copyState === 'ok' ? (
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : copyState === 'err' ? (
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
          </svg>
        ) : (
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
        )}
      </button>
      {/* L'échec est dit à l'écran, pas seulement en infobulle : une infobulle
          ne s'ouvre que si l'utilisateur revient survoler le bouton. Le badge
          vit dans la barre d'état, en bas de la colonne : la bulle s'ouvre donc
          vers le haut, sinon elle sortirait par le bas de la fenêtre. */}
      {copyState === 'err' && (
        <span
          role="status"
          className="absolute bottom-full left-0 mb-1.5 z-50 w-56 px-2 py-1.5 rounded border border-amber-500/40 bg-amber-500/15 text-amber-100 text-[9px] font-mono leading-snug backdrop-blur-sm"
        >
          Copie impossible dans ce navigateur : sélectionnez le numéro de version à gauche et copiez-le à la main.
        </span>
      )}
      {updateAvailable && (
        <button
          onClick={() => window.location.reload()}
          title="Nouvelle version disponible — cliquer pour recharger l'application"
          aria-label="Nouvelle version disponible — cliquer pour recharger l'application"
          className="w-4 h-4 rounded-full flex items-center justify-center animate-pulse shrink-0"
          style={{
            backgroundColor: 'rgba(var(--forge-accent), 0.2)',
            boxShadow: '0 0 8px rgba(var(--forge-accent), 0.4), inset 0 0 0 1px rgba(var(--forge-accent), 0.5)',
            color: 'rgba(var(--forge-accent), 1)',
          }}
        >
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 19V5M5 12l7-7 7 7" />
          </svg>
        </button>
      )}
    </div>
  );
}
